import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { matchGear, hasKites } from '@/lib/gearMatch';
import { Wind, MapPin, Waves, Target } from 'lucide-react';

const LEVEL_COLORS = { beginner: '#2EE59D', intermediate: '#48CAE4', advanced: '#FF6B35', all: '#B9C4CF' };

const BADGE = {
  good: { color: '#2EE59D', key: 'good' },
  warn: { color: '#FFB020', key: 'warn' },
  none: { color: '#FF6B35', key: 'none' },
};

export const SpotCard = ({ spot, onClick, nowKn, user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const go = onClick || (() => navigate(`/spots/${spot.id}`));
  const levelLabel = spot.level === 'all' ? t('spotCard.allLevels') : t(`levels.${spot.level}`, { defaultValue: spot.level });

  // Live "does my gear fit" badge — only when logged in with at least one kite.
  let badge = null;
  if (user && hasKites(user.equipment) && nowKn != null) {
    const m = matchGear(user.equipment, {
      windKnots: nowKn, weightKg: user.weight_kg, skill: user.skill_level, spotType: spot.type,
    });
    if (m && !m.rec.rideable) {
      badge = { color: '#6b7280', text: t('gearMatch.badge.light') };
    } else if (m) {
      const lvl = m.verdict.level === 'empty' ? 'none' : m.verdict.level;
      const cfg = BADGE[lvl] || BADGE.none;
      const text = cfg.key === 'none'
        ? t('gearMatch.badge.none')
        : t(`gearMatch.badge.${cfg.key}`, { size: m.verdict.best?.size });
      badge = { color: cfg.color, text };
    }
  }

  return (
    <button onClick={go} className="kite-card text-left overflow-hidden group w-full" data-testid={`spot-card-${spot.id}`}>
      <div className="relative h-40 overflow-hidden">
        <img src={spot.photos?.[0]} alt={spot.name} loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0) 30%, rgba(10,10,10,0.85) 100%)' }} />
        <span className="absolute top-3 left-3 label-eyebrow px-2 py-1 rounded-full bg-black/50 backdrop-blur border border-white/10"
          style={{ color: LEVEL_COLORS[spot.level] }}>{levelLabel}</span>
        {badge && (
          <span
            className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-black/55 backdrop-blur border"
            style={{ color: badge.color, borderColor: `${badge.color}55` }}
            data-testid={`spot-card-gear-${spot.id}`}
          >
            <Target className="w-3 h-3" /> {badge.text}
          </span>
        )}
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="font-display text-2xl leading-none">{spot.name}</h3>
          <div className="flex items-center gap-1 text-xs text-white/80 mt-1"><MapPin className="w-3 h-3" /> {spot.country}</div>
        </div>
      </div>
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Waves className="w-4 h-4 text-[#48CAE4]" /> {t(`spotCard.types.${spot.type}`, { defaultValue: spot.type })}
        </div>
        <div className="flex items-center gap-1 font-data text-xs text-muted-foreground">
          <Wind className="w-3.5 h-3.5 text-[#00B4D8]" />
          {nowKn != null ? <span className="text-[#00B4D8]">{Math.round(nowKn)}kn</span> : (spot.wind_directions || []).join(' · ')}
        </div>
      </div>
    </button>
  );
};
